import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, User, LogOut, Bell } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navbar = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setNotifications(res.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      reservation_created: '🚗',
      reservation_approved: '✅',
      reservation_rejected: '❌',
      reservation_completed: '🎉',
      car_request_created: '📝',
      car_request_updated: '🔄'
    };
    return icons[type] || '📬';
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <span className="text-2xl font-bold font-heading">J</span>
            </div>
            <div>
              <div className="text-lg font-heading font-bold tracking-wider">JAMOH & CO</div>
              <div className="text-xs text-textSecondary">AUTOS LTD</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/marketplace" className="text-textPrimary hover:text-primary transition-colors">Browse Cars</Link>
            <Link to="/auctions" className="text-textPrimary hover:text-primary transition-colors">Auctions</Link>
            <Link to="/request-vehicle" className="text-textPrimary hover:text-primary transition-colors">Request a Car</Link>
            <Link to="/calculator" className="text-textPrimary hover:text-primary transition-colors">Calculator</Link>
            
            {user ? (
              <>
                {/* Notification Bell */}
                <div className="relative" ref={notifRef}>
                  <button
                    onClick={() => {
                      setShowNotifications(!showNotifications);
                      if (!showNotifications) fetchNotifications();
                    }}
                    className="relative text-textPrimary hover:text-primary transition-colors"
                  >
                    <Bell className="w-6 h-6" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-96 glass rounded-sm shadow-lg max-h-96 overflow-y-auto z-50">
                      <div className="p-4 border-b border-white/10 flex items-center justify-between">
                        <h3 className="font-heading font-bold">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-primary hover:text-primaryHover"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                      
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-textSecondary">
                          No notifications yet
                        </div>
                      ) : (
                        <div>
                          {notifications.map((notif) => (
                            <div
                              key={notif.notification_id}
                              onClick={() => !notif.is_read && markAsRead(notif.notification_id)}
                              className={`p-4 border-b border-white/10 cursor-pointer hover:bg-white/5 transition-colors ${
                                !notif.is_read ? 'bg-primary/10' : ''
                              }`}
                            >
                              <div className="flex items-start space-x-3">
                                <span className="text-2xl">{getNotificationIcon(notif.type)}</span>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-bold text-sm">{notif.title}</span>
                                    {!notif.is_read && (
                                      <span className="w-2 h-2 bg-primary rounded-full"></span>
                                    )}
                                  </div>
                                  <p className="text-sm text-textSecondary">{notif.message}</p>
                                  <span className="text-xs text-textSecondary">
                                    {new Date(notif.created_at).toLocaleDateString()} {new Date(notif.created_at).toLocaleTimeString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Link to="/dashboard" className="text-textPrimary hover:text-primary transition-colors">
                  <User className="inline w-5 h-5" /> Dashboard
                </Link>
                {(user.role === 'dealer' && user.approval_status === 'approved') || user.role === 'admin' ? (
                  <Link to="/dealer-dashboard" className="text-textPrimary hover:text-primary transition-colors">Dealer Panel</Link>
                ) : null}
                {user.role === 'admin' && (
                  <Link to="/admin" className="text-primary hover:text-primaryHover transition-colors font-bold">Admin</Link>
                )}
                <button
                  onClick={logout}
                  data-testid="logout-button"
                  className="flex items-center space-x-2 bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase px-4 py-2 transition-all duration-300 red-glow"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="login-link"
                className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase px-6 py-2 transition-all duration-300 red-glow"
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-4">
            <Link to="/marketplace" className="block text-textPrimary hover:text-primary transition-colors">Browse Cars</Link>
            <Link to="/auctions" className="block text-textPrimary hover:text-primary transition-colors">Auctions</Link>
            <Link to="/calculator" className="block text-textPrimary hover:text-primary transition-colors">Import Calculator</Link>
            
            {user ? (
              <>
                <Link to="/dashboard" className="block text-textPrimary hover:text-primary transition-colors">Dashboard</Link>
                {(user.role === 'dealer' && user.approval_status === 'approved') || user.role === 'admin' ? (
                  <Link to="/dealer-dashboard" className="block text-textPrimary hover:text-primary transition-colors">Dealer Panel</Link>
                ) : null}
                {user.role === 'admin' && (
                  <Link to="/admin" className="block text-primary hover:text-primaryHover transition-colors font-bold">Admin</Link>
                )}
                <button
                  onClick={logout}
                  className="block w-full text-left text-textPrimary hover:text-primary transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" className="block text-textPrimary hover:text-primary transition-colors">Login</Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;